import React, { useContext, Fragment, useMemo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { PatientChartContext } from '../../../contexts/patientChart';
import { PATIENT } from '../../../constants/patientChart.constant';
import { removeXSSAttacks } from '../../../common/helper';
import { FEATURE } from '../../../utils/feature';

const hasFeature = FEATURE();

const DefaultCodeStatus = {
  uniqueCodes: [],
  uniqueCodesString: 'NONE',
};

/**
 * @param {{
 *  icd10Codes: {
 *    data: {
 *      OrderId: number;
 *      ICDCodes: {
 *        ICDCode: string;
 *        ICDDesc: string;
 *      }[];
 *    }[];
 *    called: boolean;
 *    loading: boolean;
 *  };
 * }} param
 * @returns {React.JSX.Element}
 */
export const StorefrontHistory = ({
  patient = PATIENT(),
  icd10Codes,
}) => {
  const { storefrontHistory: { medications } } = useContext(PatientChartContext);

  /**
   * @type {{
   *  uniqueCodes: string[];
   *  uniqueCodesString: string;
   * }}
   */
  const { uniqueCodesString } = useMemo(() => {
    if (!icd10Codes.called) {
      return DefaultCodeStatus;
    }

    if (icd10Codes.loading) {
      return DefaultCodeStatus;
    }

    if (!icd10Codes.data.length) {
      return DefaultCodeStatus;
    }

    /**
     * @type {Map<string, {
     *  ICDCode: string;
     *  ICDDesc: string;
     * }>}
     */
    const _uniqueCodes = new Map();
    const codes = icd10Codes.data.flatMap((item) => item.ICDCodes);
    codes.forEach((code) => {
      _uniqueCodes.set(code.ICDCode, code);
    });

    const uC = [..._uniqueCodes.keys()];
    const uCS = uC.join(', ');
    return {
      uniqueCodes: uC,
      uniqueCodesString: uCS,
    };
  }, [icd10Codes]);

  const LoadingTemplate = (
    <div className='flex flex-column'>
      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-100 width-100' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>

      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-140 width-140' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>
    </div>
  );

  const MedicationsTemplate = new Map([
    [true, (
      <Fragment>
        <div className='flex align-items-flex-start m-b-1'>
          <div className='flex'>Medications</div>
        </div>
        <div className='flex align-items-center m-b-5'>
          <div
            className='flex flex-fill border-primary-v4-2 p-2 font-proxima-nova font-16 color-dark-grey border-box min-height-36'
            dangerouslySetInnerHTML={{
              __html: removeXSSAttacks(medications),
            }}
          />
        </div>
      </Fragment>
    )],
    [false, <></>],
  ]);

  const ICD10CodesTemplate = new Map([
    [true, (
      <Fragment>
        <div className='flex align-items-flex-start m-b-1'>
          <div className='flex'>Patient ICD Codes</div>
        </div>
        <div className='flex align-items-center'>
          <div
            className='flex flex-fill border-primary-v4-2 p-2 font-proxima-nova font-16 color-dark-grey border-box min-height-36'
            dangerouslySetInnerHTML={{
              __html: removeXSSAttacks(uniqueCodesString),
            }}
          />
        </div>
      </Fragment>
    )],
    [false, <></>],
  ]);

  const HistoryTemplate = (
    <Fragment>
      {MedicationsTemplate.get(true)}
      {ICD10CodesTemplate.get(hasFeature.ICD10CODES)}
    </Fragment>
  );

  const HistoryTemplateCollection = new Map([
    ['false-false', LoadingTemplate],
    ['false-true', LoadingTemplate],
    ['true-true', LoadingTemplate],
    ['true-false', HistoryTemplate],
  ]);

  return HistoryTemplateCollection.get(`${patient.api.called}-${patient.api.loading}`);
};

export default StorefrontHistory;
